import { debounce, onDocumentReady } from '@theme/utilities';

const TARGET_SELECTOR = '.menu-drawer__localization';
const WIDGET_SELECTOR = '.buckscc-currency-wrapper, .buckscc-currency-box';
const RESIZE_DEBOUNCE_MS = 100; // matches header-menu.js's own resize→setHeaderMenuStyle cadence

/**
 * The bucksCC SDK picks "mobile" vs "desktop" placement using its own
 * matchMedia checks (viewport width + orientation), which don't agree with
 * this theme's touch/overflow-based drawer breakpoint. That mismatch makes
 * it silently skip inserting before .menu-drawer__localization.
 *
 * This checks the theme's own `data-menu-style` state (set by
 * setHeaderMenuStyle() in utilities.js) rather than .menu-drawer__localization's
 * own visibility, because that element sits inside a native <details> that's
 * display:none until the drawer is actually opened — checking it directly
 * would only ever allow relocation after the user opens the drawer.
 */
function isMobileDrawerActive() {
  const headerComponent = document.querySelector('#header-component');
  return /** @type {HTMLElement | null} */ (headerComponent)?.dataset.menuStyle === 'drawer';
}

function relocateWidget() {
  if (!isMobileDrawerActive()) return;

  const target = document.querySelector(TARGET_SELECTOR);
  if (!target) return;

  const widget = document.querySelector(WIDGET_SELECTOR);
  if (!widget) return; // not created yet — MutationObserver below will catch it later

  if (widget.nextElementSibling === target) return; // already correctly placed

  target?.parentElement?.insertBefore(widget, target);
}

const debouncedRelocate = debounce(relocateWidget, RESIZE_DEBOUNCE_MS);

function init() {
  // Set synchronously by the app embed's own inline script in <head>, which
  // runs before this deferred module. If the embed is disabled in the theme
  // editor, `window.bucksCC` won't exist at all; if it's enabled but the app
  // itself is inactive, `metaConfig.active` will be false. Either way, no
  // widget will ever be created, so skip installing observers/listeners
  // entirely rather than leaving a page-wide MutationObserver running for
  // nothing.
  // @ts-ignore - window.bucksCC is set by the third-party bucksCC app embed's own inline script; not ours to type.
  if (!window.bucksCC?.metaConfig?.active) return;

  window.addEventListener('BUCKSCC-LOADED', relocateWidget);
  window.addEventListener('resize', debouncedRelocate);
  window.addEventListener('orientationchange', debouncedRelocate);

  const headerComponent = document.querySelector('#header-component');
  if (headerComponent) {
    new MutationObserver(() => {
      if (/** @type {HTMLElement} */ (headerComponent).dataset.menuStyle === 'drawer') relocateWidget();
    }).observe(headerComponent, { attributes: true, attributeFilter: ['data-menu-style'] });
  }

  // Catches the widget being created/recreated asynchronously anywhere in
  // <body> — the SDK's init chain involves network round trips, and its own
  // rerender() detaches and recreates the widget on every resize.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        relocateWidget();
        break;
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  relocateWidget();
}

onDocumentReady(init);
